import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import { queueAction } from '@/lib/offlineDb';

/**
 * useFMSop - Hook for fetching and managing daily SOP tasks.
 * On first load, calls POST /api/fm/sop/generate to ensure tasks exist (idempotent),
 * then fetches from GET /api/fm/sop for the structured response.
 *
 * Returns: { tasks, overdue, summary, loading, error, refetch, completeTask, uncompleteTask }
 */
export function useFMSop(gymId, date) {
  const [tasks, setTasks] = useState({ morning_peak: [], off_peak: [], evening_peak: [] });
  const [overdue, setOverdue] = useState([]);
  const [summary, setSummary] = useState({ total: 0, completed: 0, pending: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const generatedRef = useRef(false);

  const fetchTasks = useCallback(async () => {
    if (!gymId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Generate tasks (idempotent) on first load
      if (!generatedRef.current) {
        await axiosInstance.post('/fm/sop/generate', {
          gym_id: gymId,
          date: date || undefined,
          is_physical: true,
        });
        generatedRef.current = true;
      }

      // Step 2: Fetch structured task list
      const params = { gym_id: gymId };
      if (date) params.date = date;
      const res = await axiosInstance.get('/fm/sop', { params });

      setTasks(res.data.tasks || { morning_peak: [], off_peak: [], evening_peak: [] });
      setOverdue(res.data.overdue || []);
      setSummary(res.data.summary || { total: 0, completed: 0, pending: 0, overdue: 0 });
    } catch (err) {
      console.error('useFMSop fetch error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load SOP tasks');
    } finally {
      setLoading(false);
    }
  }, [gymId, date]);

  useEffect(() => {
    generatedRef.current = false; // Reset when gymId changes
    fetchTasks();
  }, [fetchTasks]);

  /**
   * Mark task as complete with optimistic update.
   */
  const completeTask = useCallback(async (taskId) => {
    // Optimistic update: find task in today's tasks or overdue, mark completed
    const previousTasks = { ...tasks };
    const previousOverdue = [...overdue];
    const previousSummary = { ...summary };

    // Update local state optimistically
    const updateTaskStatus = (taskList) =>
      taskList.map((t) =>
        t.task_id === taskId
          ? { ...t, status: 'completed', completed_at: new Date().toISOString() }
          : t
      );

    setTasks({
      morning_peak: updateTaskStatus(tasks.morning_peak),
      off_peak: updateTaskStatus(tasks.off_peak),
      evening_peak: updateTaskStatus(tasks.evening_peak),
    });
    setOverdue(updateTaskStatus(overdue));

    // Update summary optimistically
    setSummary((prev) => ({
      ...prev,
      completed: prev.completed + 1,
      pending: Math.max(0, prev.pending - 1),
    }));

    if (!navigator.onLine) {
      // Queue for later sync
      await queueAction({
        type: 'sop_complete',
        method: 'put',
        url: `/fm/sop/${taskId}/complete`,
        data: {},
      });
      return;
    }
    try {
      await axiosInstance.put(`/fm/sop/${taskId}/complete`);
    } catch (err) {
      console.error('Complete task error:', err);
      // Revert on error
      setTasks(previousTasks);
      setOverdue(previousOverdue);
      setSummary(previousSummary);
    }
  }, [tasks, overdue, summary]);

  /**
   * Uncomplete task (undo) with optimistic update.
   */
  const uncompleteTask = useCallback(async (taskId) => {
    const previousTasks = { ...tasks };
    const previousOverdue = [...overdue];
    const previousSummary = { ...summary };

    const updateTaskStatus = (taskList) =>
      taskList.map((t) =>
        t.task_id === taskId
          ? { ...t, status: 'pending', completed_at: null }
          : t
      );

    setTasks({
      morning_peak: updateTaskStatus(tasks.morning_peak),
      off_peak: updateTaskStatus(tasks.off_peak),
      evening_peak: updateTaskStatus(tasks.evening_peak),
    });
    setOverdue(updateTaskStatus(overdue));

    setSummary((prev) => ({
      ...prev,
      completed: Math.max(0, prev.completed - 1),
      pending: prev.pending + 1,
    }));

    if (!navigator.onLine) {
      await queueAction({
        type: 'sop_uncomplete',
        method: 'put',
        url: `/fm/sop/${taskId}/uncomplete`,
        data: {},
      });
      return;
    }
    try {
      await axiosInstance.put(`/fm/sop/${taskId}/uncomplete`);
    } catch (err) {
      console.error('Uncomplete task error:', err);
      setTasks(previousTasks);
      setOverdue(previousOverdue);
      setSummary(previousSummary);
    }
  }, [tasks, overdue, summary]);

  return {
    tasks,
    overdue,
    summary,
    loading,
    error,
    refetch: fetchTasks,
    completeTask,
    uncompleteTask,
  };
}
